import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { stateOptions } from "data";
import { TextInput } from "../../../runQuote/components/TextInput";
import { FormPhoneInput } from "../../../runQuote/components/FormPhoneInput";
import { DropdownInput } from "../../../runQuote/components/DropdownInput";
import { DatePicker } from "../../../runQuote/components/DatePicker";
import { SwitchInput } from "../../../runQuote/components/SwitchInput";

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
];

const MARITAL_STATUS = [
  { value: "Single", label: "Single" },
  { value: "Married", label: "Married" },
  { value: "Separated", label: "Separated" },
  { value: "Widowed", label: "Widowed" },
  { value: "Divorced", label: "Divorced" },
];

const RESIDENCE_TYPES = [
  { value: "Apartment", label: "Apartment" },
  { value: "Condo", label: "Condo" },
  { value: "Duplex", label: "Duplex" },
  { value: "Mobile Home", label: "Mobile Home" },
  { value: "Quadplex", label: "Quadplex" },
  { value: "Row House Center", label: "Row House Center" },
  { value: "Row House End", label: "Row House End" },
  { value: "Single Family", label: "Single Family" },
  { value: "Town House Center", label: "Town House Center" },
  { value: "Town House End", label: "Town House End" },
  { value: "Triplex", label: "Triplex" },
];

const PERSONAL_PROPERTY = [
  { value: "5k", label: "5k" },
  { value: "10k", label: "10k" },
  { value: "20k", label: "20k" },
  { value: "30k", label: "30k" },
  { value: "40k", label: "40k" },
  { value: "50k", label: "50k" },
  { value: "60k", label: "60k" },
  { value: "70k", label: "70k" },
  { value: "80k", label: "80k" },
  { value: "90k", label: "90k" },
  { value: "100k", label: "100k" },
];

const RELATIONSHIP_OPTIONS = [
  { value: "Parent", label: "Parent" },
  { value: "Child", label: "Child" },
  { value: "Other", label: "Other" },
];

export function RentersInsuredInfo() {
  const {
    control,
    register,
    setValue,
    getValues,
    trigger,
    clearErrors,
    formState: { dirtyFields, errors },
  } = useFormContext();
  const [openDob, setOpenDob] = React.useState(false);
  const [openDemoDob, setOpenDemoDob] = React.useState(false);
  const [openAdditionalDob, setOpenAdditionalDob] = React.useState(false);

  const movedRecently = useWatch({ control, name: "step2.movedRecently" });
  const insuredFirstName = useWatch({ control, name: "step2.firstName" });
  const insuredLastName = useWatch({ control, name: "step2.lastName" });
  const insuredDob = useWatch({ control, name: "step2.dateOfBirth" });
  const insuredGender = useWatch({ control, name: "step2.gender" });
  const insuredState = useWatch({ control, name: "step2.state" });
  const insuredZip = useWatch({ control, name: "step2.zip" });
  const priorState = useWatch({ control, name: "step2.priorState" });
  const priorZip = useWatch({ control, name: "step2.priorZip" });
  const demoFirstDirty = !!dirtyFields?.step2?.demographicFirstName;
  const demoLastDirty = !!dirtyFields?.step2?.demographicLastName;
  const demoDobDirty = !!dirtyFields?.step2?.demographicDateOfBirth;
  const demoGenderDirty = !!dirtyFields?.step2?.demographicGender;
  const zipErrorMessage = errors?.step2?.zip?.message;
  const priorZipErrorMessage = errors?.step2?.priorZip?.message;

  React.useEffect(() => {
    const currentDemoFirst = getValues("step2.demographicFirstName");
    const nextDemoFirst = insuredFirstName ?? "";
    if (!demoFirstDirty && currentDemoFirst !== nextDemoFirst) {
      setValue("step2.demographicFirstName", nextDemoFirst, { shouldDirty: false });
    }
    const currentDemoLast = getValues("step2.demographicLastName");
    const nextDemoLast = insuredLastName ?? "";
    if (!demoLastDirty && currentDemoLast !== nextDemoLast) {
      setValue("step2.demographicLastName", nextDemoLast, { shouldDirty: false });
    }
    const currentDemoDob = getValues("step2.demographicDateOfBirth");
    if (!demoDobDirty && currentDemoDob !== insuredDob) {
      setValue("step2.demographicDateOfBirth", insuredDob, { shouldDirty: false });
    }
    const currentDemoGender = getValues("step2.demographicGender");
    const nextDemoGender = insuredGender ?? "";
    if (!demoGenderDirty && currentDemoGender !== nextDemoGender) {
      setValue("step2.demographicGender", nextDemoGender, { shouldDirty: false });
    }
  }, [
    insuredFirstName,
    insuredLastName,
    insuredDob,
    insuredGender,
    demoFirstDirty,
    demoLastDirty,
    demoDobDirty,
    demoGenderDirty,
    getValues,
    setValue,
  ]);

  React.useEffect(() => {
    if (insuredState && insuredZip) {
      void trigger("step2.zip");
    }
  }, [insuredState, insuredZip, trigger]);

  React.useEffect(() => {
    if (insuredState && insuredZip) return;
    if (zipErrorMessage === "This zip code is not valid for this state.") {
      clearErrors("step2.zip");
    }
  }, [insuredState, insuredZip, clearErrors, zipErrorMessage]);

  React.useEffect(() => {
    if (movedRecently && priorState && priorZip) {
      void trigger("step2.priorZip");
    }
  }, [movedRecently, priorState, priorZip, trigger]);

  React.useEffect(() => {
    if (movedRecently && priorState && priorZip) return;
    if (priorZipErrorMessage === "This zip code is not valid for this state.") {
      clearErrors("step2.priorZip");
    }
  }, [movedRecently, priorState, priorZip, clearErrors, priorZipErrorMessage]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Insured</h2>

      <div className="grid gap-6 md:grid-cols-2">
        <TextInput
          register={register}
          label="First Name"
          id="insuredFirstName"
          stepName="step2.firstName"
          stepId="step2"
        />
        <TextInput
          register={register}
          label="Last Name"
          id="insuredLastName"
          stepName="step2.lastName"
          stepId="step2"
        />
        <DatePicker
          control={control}
          open={openDob}
          setOpen={setOpenDob}
          label="Date of Birth"
          name="step2.dateOfBirth"
          step="step2"
        />
        <DropdownInput
          control={control}
          label="Gender"
          dropdownLabel="Select Gender"
          stepName="step2.gender"
          stepId="step2"
          itemList={GENDER_OPTIONS}
        />
        <TextInput
          register={register}
          label="Insured Email"
          id="insuredEmail"
          stepName="step2.email"
          stepId="step2"
        />
        <FormPhoneInput
          register={register}
          setValue={setValue}
          label="Insured Phone Number"
          id="insuredPhone"
          stepName="step2.phone"
          stepId="step2"
        />
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900">Mailing Address</h3>
        <div className="grid gap-6 md:grid-cols-2">
          <TextInput
            register={register}
            label="Address"
            id="insuredAddress1"
            stepName="step2.address1"
            stepId="step2"
          />
          <TextInput
            register={register}
            label="Address Line 2 (Apt #, etc.)"
            id="insuredAddress2"
            stepName="step2.address2"
            stepId="step2"
          />
          <TextInput
            register={register}
            label="City"
            id="insuredCity"
            stepName="step2.city"
            stepId="step2"
          />
          <DropdownInput
            control={control}
            label="State"
            dropdownLabel="Select State"
            stepName="step2.state"
            stepId="step2"
            itemList={stateOptions}
          />
          <TextInput
            register={register}
            label="Zip"
            id="insuredZip"
            stepName="step2.zip"
            stepId="step2"
          />
        </div>
      </div>

      <div className="space-y-4">
        <SwitchInput
          control={control}
          id="movedRecently"
          stepName="step2.movedRecently"
          stepId="step2"
          label="Has the insured moved in the past 2 months?"
        />

        {movedRecently && (
          <div className="grid gap-6 md:grid-cols-2">
            <TextInput
              register={register}
              label="Prior Mailing Address"
              id="priorAddress"
              stepName="step2.priorAddress"
              stepId="step2"
            />
            <TextInput
              register={register}
              label="Prior City"
              id="priorCity"
              stepName="step2.priorCity"
              stepId="step2"
            />
            <DropdownInput
              control={control}
              label="Prior State"
              dropdownLabel="Select State"
              stepName="step2.priorState"
              stepId="step2"
              itemList={stateOptions}
            />
            <TextInput
              register={register}
              label="Prior Zip"
              id="priorZip"
              stepName="step2.priorZip"
              stepId="step2"
            />
          </div>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <DropdownInput
          control={control}
          label="Residence Type"
          dropdownLabel="Select"
          stepName="step2.residenceType"
          stepId="step2"
          itemList={RESIDENCE_TYPES}
        />
        <DropdownInput
          control={control}
          label="Personal Property Amount"
          dropdownLabel="Select"
          stepName="step2.personalPropertyAmount"
          stepId="step2"
          itemList={PERSONAL_PROPERTY}
        />
      </div>

      <div className="space-y-2">
        <SwitchInput
          control={control}
          id="restrictedDogs"
          stepName="step2.hasRestrictedDogs"
          stepId="step2"
          label="Does the insured have dogs such as the following?"
        />
        <p className="text-sm text-gray-500">
          Akitas, American Bulldogs, Chow Chow, Doberman Pinschers, Mastiffs, Pit Bulls,
          Rottweilers, Staffordshire Terriers, Wolf Hybrids, or any mix or variation of these
          breeds, or any dog with a history of biting, or any dog underwriting deems ineligible
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900">Demographic</h3>
        <div className="grid gap-6 md:grid-cols-2">
          <TextInput
            register={register}
            label="First Name"
            id="demographicFirstName"
            stepName="step2.demographicFirstName"
            stepId="step2"
          />
          <TextInput
            register={register}
            label="Last Name"
            id="demographicLastName"
            stepName="step2.demographicLastName"
            stepId="step2"
          />
          <DropdownInput
            control={control}
            label="Marital Status"
            dropdownLabel="Select"
            stepName="step2.demographicMaritalStatus"
            stepId="step2"
            itemList={MARITAL_STATUS}
          />
          <DatePicker
            control={control}
            open={openDemoDob}
            setOpen={setOpenDemoDob}
            label="Date of Birth"
            name="step2.demographicDateOfBirth"
            step="step2"
          />
          <DropdownInput
            control={control}
            label="Gender"
            dropdownLabel="Select Gender"
            stepName="step2.demographicGender"
            stepId="step2"
            itemList={GENDER_OPTIONS}
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900">
          Add an additional person to this policy (optional)
        </h3>
        <div className="grid gap-6 md:grid-cols-2">
          <TextInput
            register={register}
            label="First Name"
            id="additionalFirstName"
            stepName="step2.additionalFirstName"
            stepId="step2"
          />
          <TextInput
            register={register}
            label="Last Name"
            id="additionalLastName"
            stepName="step2.additionalLastName"
            stepId="step2"
          />
          <DropdownInput
            control={control}
            label="Marital Status"
            dropdownLabel="Select"
            stepName="step2.additionalMaritalStatus"
            stepId="step2"
            itemList={MARITAL_STATUS}
          />
          <DropdownInput
            control={control}
            label="Relationship"
            dropdownLabel="Select"
            stepName="step2.additionalRelationship"
            stepId="step2"
            itemList={RELATIONSHIP_OPTIONS}
          />
          <DropdownInput
            control={control}
            label="Gender"
            dropdownLabel="Select Gender"
            stepName="step2.additionalGender"
            stepId="step2"
            itemList={GENDER_OPTIONS}
          />
          <DatePicker
            control={control}
            open={openAdditionalDob}
            setOpen={setOpenAdditionalDob}
            label="Date of Birth"
            name="step2.additionalDateOfBirth"
            step="step2"
          />
          <SwitchInput
            control={control}
            id="additionalSecondNamedInsured"
            stepName="step2.additionalSecondNamedInsured"
            stepId="step2"
            label="Should this person be listed as the 2nd named insured?"
          />
        </div>
      </div>
    </div>
  );
}
